import { useEffect, useMemo, useState } from 'react';
import { localeUI } from '../lib/format';
import { Plus, Trash2, User } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { NoCargo } from './NoCargo';
import { useFallo } from '../lib/useFallo';
import { PhoneLink } from './PhoneLink';
import { useAuth, hasRole } from '../lib/auth';

export type Adjuster = {
  id: string; title: string; title_label: string | null;
  name: string | null; email: string | null; phone: string | null; created_at: string; added_by?: string | null;
};

const TITLE_OPTS = [
  { v: 'FA', key: 'adj.fa' },
  { v: 'DA', key: 'adj.da' },
  { v: 'Manager', key: 'adj.manager' },
  { v: 'Other', key: 'adj.other' },
] as const;

/** Etiqueta corta del título (para el badge). */
export function adjusterTitleShort(a: { title: string; title_label: string | null }): string {
  if (a.title === 'Other') return a.title_label || 'Other';
  return a.title; // FA / DA / Manager
}

/**
 * Adjuster info del claim: LISTA de ajustadores estructurados. Cada uno con título
 * (FA / DA / Manager / Other+nombre), nombre, email y teléfono + la fecha en que se
 * agregó (para saber quién es el nuevo). Se agregan/borran uno a uno.
 */
export function AdjusterInfoBox({ claimId, compact }: { claimId: string; initial?: string | null; compact?: boolean }) {
  const { t } = useI18n();
  const { me } = useAuth();
  const canDelete = hasRole(me, 'super-manager');
  const [list, setList] = useState<Adjuster[]>([]);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState<string>('FA');
  const [titleLabel, setTitleLabel] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const { fallo, marcar, limpiar } = useFallo();
  const load = () => api.get<{ adjusters: Adjuster[] }>(`/api/claims/${claimId}/adjusters`).then((r) => setList(r.adjusters)).then(limpiar).catch(marcar);
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [claimId]);

  const fmt = (iso: string) => new Date(iso).toLocaleDateString(localeUI(), { month: 'short', day: 'numeric', year: 'numeric' });

  // Agrupa por tipo (FA/DA/Manager/Other-label). `list` viene ordenada del más nuevo
  // al más viejo → el primero de cada grupo es el ACTIVO, el resto es historial.
  const groups = useMemo(() => {
    const m = new Map<string, Adjuster[]>();
    for (const a of list) {
      const key = adjusterTitleShort(a);
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(a);
    }
    return [...m.entries()].map(([key, arr]) => ({ key, active: arr[0], history: arr.slice(1) }));
  }, [list]);

  async function add() {
    if (!name.trim() && !email.trim() && !phone.trim()) return;
    setBusy(true);
    try {
      await api.post(`/api/claims/${claimId}/adjusters`, { title, titleLabel: title === 'Other' ? titleLabel.trim() || null : null, name, email, phone });
      setName(''); setEmail(''); setPhone(''); setTitleLabel(''); setTitle('FA'); setAdding(false); await load();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }
  async function del(id: string) {
    if (!window.confirm(t('adj.delConfirm'))) return;
    try { await api.del(`/api/claims/${claimId}/adjusters/${id}`); await load(); } catch { /* */ }
  }

  return (
    <div style={{ marginTop: compact ? 8 : 14, minWidth: 0 }}>
      {fallo && <NoCargo onRetry={load} compact />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span className="muted" style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}><User size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('adj.title')}</span>
        {!adding && <button className="btn ghost sm" style={{ padding: '0 8px', fontSize: 'var(--fs-2xs)' }} onClick={() => setAdding(true)}><Plus size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('adj.add')}</button>}
      </div>

      {/* Un ajustador ACTIVO por tipo (el más reciente); los anteriores quedan en el
          desplegable con su fecha + quién lo agregó. Borrar: solo super-manager. */}
      {groups.length > 0 && (
        <div style={{ display: 'grid', gap: 6, marginTop: 6 }}>
          {groups.map(({ key, active, history }) => {
            const open = openGroups.has(key);
            const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 'var(--fs-sm)', background: 'var(--surface-2)', borderRadius: 8, padding: '6px 8px' };
            return (
              <div key={key} style={{ display: 'grid', gap: 4 }}>
                <div style={rowStyle}>
                  <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--accent-soft)', color: 'var(--accent)' }}>{adjusterTitleShort(active)}</span>
                  {active.name && <strong style={{ fontWeight: 600 }}>{active.name}</strong>}
                  {active.email && <a href={`mailto:${active.email}`} style={{ color: 'var(--accent)' }}>{active.email}</a>}
                  {active.phone && <PhoneLink phone={active.phone} claimId={claimId} recipientType="insurance" className="muted" />}
                  <span style={{ flex: 1 }} />
                  {history.length > 0 && (
                    <button className="btn ghost sm" style={{ padding: '0 6px', fontSize: 'var(--fs-2xs)' }} onClick={() => setOpenGroups((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; })}>
                      {open ? '▾' : '▸'} {t('adj.previous')} ({history.length})
                    </button>
                  )}
                  <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{fmt(active.created_at)}{active.added_by ? ` · ${active.added_by}` : ''}</span>
                  {canDelete && <button className="btn ghost sm" style={{ color: 'var(--red)', padding: '0 6px' }} onClick={() => del(active.id)} title={t('adj.del')}><Trash2 size={13} strokeWidth={1.75} /></button>}
                </div>
                {open && history.map((h) => (
                  <div key={h.id} style={{ ...rowStyle, opacity: 0.7, marginLeft: 14, background: 'var(--bg)' }}>
                    <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{t('adj.old')}</span>
                    {h.name && <span>{h.name}</span>}
                    {h.email && <a href={`mailto:${h.email}`} style={{ color: 'var(--accent)' }}>{h.email}</a>}
                    {h.phone && <PhoneLink phone={h.phone} claimId={claimId} recipientType="insurance" className="muted" />}
                    <span style={{ flex: 1 }} />
                    <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{fmt(h.created_at)}{h.added_by ? ` · ${h.added_by}` : ''}</span>
                    {canDelete && <button className="btn ghost sm" style={{ color: 'var(--red)', padding: '0 6px' }} onClick={() => del(h.id)} title={t('adj.del')}><Trash2 size={13} strokeWidth={1.75} /></button>}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
      {list.length === 0 && !adding && <div className="muted" style={{ fontSize: 'var(--fs-sm)', marginTop: 2 }}>{t('adj.empty')}</div>}

      {/* Form para agregar un ajustador. */}
      {adding && (
        <div style={{ marginTop: 8, border: '1px solid var(--line)', borderRadius: 8, padding: 8, display: 'grid', gap: 6, background: 'var(--bg)' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <select className="input sm" style={{ width: 190 }} value={title} onChange={(e) => setTitle(e.target.value)}>
              {TITLE_OPTS.map((o) => <option key={o.v} value={o.v}>{t(o.key)}</option>)}
            </select>
            {title === 'Other' && <input className="input sm" style={{ flex: 1, minWidth: 140 }} placeholder={t('adj.otherPh')} value={titleLabel} onChange={(e) => setTitleLabel(e.target.value)} />}
          </div>
          <input className="input sm" placeholder={t('adj.namePh')} value={name} onChange={(e) => setName(e.target.value)} />
          <input className="input sm" type="email" placeholder={t('adj.emailPh')} value={email} onChange={(e) => setEmail(e.target.value)} />
          <input className="input sm" type="tel" placeholder={t('adj.phonePh')} value={phone} onChange={(e) => setPhone(e.target.value)} />
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn sm" disabled={busy || (!name.trim() && !email.trim() && !phone.trim())} onClick={add}>{t('common.save')}</button>
            <button className="btn ghost sm" onClick={() => { setAdding(false); setName(''); setEmail(''); setPhone(''); setTitleLabel(''); setTitle('FA'); }}>{t('common.cancel')}</button>
          </div>
        </div>
      )}
    </div>
  );
}
